// PBR image flipper — cycles stacked [data-flip-frame] children with a hard
// cut (visibility toggle, no fade) every data-interval ms. Pauses when the
// element leaves the viewport or the tab is hidden, holds the first frame
// under prefers-reduced-motion, and shows the selected frame while a merchant
// edits a block in the theme customizer.

class PbrImageFlipper extends HTMLElement {
  connectedCallback() {
    this.frames = Array.from(this.querySelectorAll('[data-flip-frame]'));
    if (this.frames.length <= 1) return;

    this.index = Math.max(
      0,
      this.frames.findIndex((f) => f.classList.contains('is-current'))
    );
    this.interval = parseInt(this.dataset.interval, 10) || 2000;
    this.inView = false;
    this.editing = false;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.inView = entry.isIntersecting;
        this.#sync();
      },
      { rootMargin: '50px' }
    );
    this.observer.observe(this);

    this.onVisibility = () => this.#sync();
    document.addEventListener('visibilitychange', this.onVisibility);

    // Theme editor: pin the selected block's frame while it's being edited.
    this.onBlockSelect = (event) => {
      const frame = event.target.closest?.('[data-flip-frame]');
      if (!frame || !this.contains(frame)) return;
      this.editing = true;
      this.#show(this.frames.indexOf(frame));
      this.#sync();
    };
    this.onBlockDeselect = (event) => {
      if (!this.contains(event.target)) return;
      this.editing = false;
      this.#sync();
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  disconnectedCallback() {
    this.#stop();
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  #sync() {
    if (this.inView && !document.hidden && !this.editing) this.#start();
    else this.#stop();
  }

  #start() {
    if (this.timer) return;
    this.timer = window.setInterval(() => {
      this.#show((this.index + 1) % this.frames.length);
    }, this.interval);
  }

  #stop() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  #show(next) {
    if (next < 0 || next === this.index) return;
    this.frames[this.index]?.classList.remove('is-current');
    this.frames[next]?.classList.add('is-current');
    this.index = next;
  }
}

if (!customElements.get('pbr-image-flipper')) {
  customElements.define('pbr-image-flipper', PbrImageFlipper);
}
